import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import AuthenticationPage from '@/features/authentication/components/AuthenticationPage';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import getCurrentUserQuery from '@/shared/hooks/user/getCurrentUser';

import useMcpConsentRequestQuery from '../hooks/useMcpConsentRequestQuery';
import useMcpConsentDecisionMutation, { McpConsentDecision } from '../hooks/useMcpConsentDecisionMutation';

/**
 * Consent screen for an MCP client (Claude, ChatGPT, ...) asking to act as the
 * user. business-api's /mcp/oauth/authorize sends the browser here with
 * `?request=<id>`; the user picks the org the token is scoped to, and the
 * approve/deny answer says where to send the browser back to the client.
 *
 * Laid out like the sign-in screens it follows, and it runs without a cloud
 * player (NO_PLAYER_PATHS in AuthContext), so it shows as soon as the user is in.
 */
const McpAuthorizePage = () => {
  const title = 'Connect MCP client';
  useDocumentTitle(title);

  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');

  const requestQuery = useMcpConsentRequestQuery(requestId);
  const decision = useMcpConsentDecisionMutation(requestId ?? '');
  const { data: { data: currentUser } = {} } = getCurrentUserQuery();

  const orgs = requestQuery.data?.orgs ?? [];
  const [orgId, setOrgId] = useState<number | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // Start on the org the user is working in, when the token can be scoped to it.
  useEffect(() => {
    if (orgId !== null || orgs.length === 0) return;
    const current = currentUser?.org?.id;
    setOrgId(orgs.some((org) => org.id === current) ? current! : orgs[0].id);
  }, [orgs, orgId, currentUser?.org?.id]);

  const decide = (choice: McpConsentDecision) =>
    decision.mutate(choice, {
      onSuccess: (redirectUrl) => {
        // Stay disabled while the browser leaves for the client's redirect_uri.
        setRedirecting(true);
        window.location.href = redirectUrl;
      },
    });

  const busy = decision.isPending || redirecting;
  const notFound = (error: unknown) => error instanceof AxiosError && error.response?.status === 404;

  const renderBody = () => {
    if (!requestId || notFound(requestQuery.error) || notFound(decision.error)) {
      return (
        <Alert severity="warning">
          This authorization request has expired or was already used. Start the connection again from
          your MCP client.
        </Alert>
      );
    }
    if (requestQuery.isError) {
      return <Alert severity="error">Could not load the authorization request.</Alert>;
    }
    if (!requestQuery.data) {
      return <CircularProgress size={24} sx={{ alignSelf: 'center' }} />;
    }

    const clientName = requestQuery.data.client.name;
    return (
      <Stack spacing={3}>
        <Typography variant="body1">
          <strong>{clientName}</strong> wants to find games and players and create clips on your behalf.
        </Typography>

        {orgs.length === 0 ? (
          <Alert severity="warning">
            You don&apos;t have MCP access in any organization. Ask an admin to grant it, then try again.
          </Alert>
        ) : (
          // Same select as the sign-in org step (OrgSelectionStep).
          <FormControl fullWidth>
            <InputLabel id="mcp-org-select-label">Organization</InputLabel>
            <Select
              disabled={busy}
              labelId="mcp-org-select-label"
              value={orgId ?? ''}
              label="Organization"
              onChange={(event) => setOrgId(event.target.value as number)}
            >
              {orgs.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {decision.isError && (
          <Alert severity="error">
            {decision.error instanceof AxiosError && decision.error.response?.status === 403
              ? 'MCP access is not granted in this organization.'
              : 'Something went wrong. Please try again.'}
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={busy || orgId === null}
            onClick={() => orgId !== null && decide({ approve: true, orgId })}
          >
            Allow
          </Button>
          <Button variant="outlined" disabled={busy} onClick={() => decide({ approve: false })}>
            Deny
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <AuthenticationPage ignoreDisableLogin>
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Typography variant="h6">{title}</Typography>
        {renderBody()}
      </Stack>
    </AuthenticationPage>
  );
};

export default McpAuthorizePage;
